import { FormEvent, useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useEffectOnce } from 'react-use';
import { BusinessGeoDto } from '../../../data-layer/business/types';
import { loadGeoSuggestsAsync } from '../../../data-layer/geo/actions';
import { selectGeo } from '../../../data-layer/geo/selectors';
import { useDispatch } from '../../../data-layer/useDispatch';
import { useSelector } from '../../../data-layer/useSelector';
import { useTranslation } from '../../../utils/useTranslation';

const DEFAULT_GEO_NAME = 'Россия';

const DEFAULT_GEO_REGION_ID = '225';

const DEFAULT_GEO = { [DEFAULT_GEO_REGION_ID]: DEFAULT_GEO_NAME };

const TIMEOUT = 200;

function getRegionsSettings(regions?: BusinessGeoDto[]) {
    if (!regions?.length) {
        return DEFAULT_GEO;
    }

    return regions.reduce<Record<string, string>>(
        (res, region) => ({
            ...res,
            [String(region.region_id)]: region.name,
        }),
        {},
    );
}

function areRegionsEqual(
    prevRegions: Record<string, string>,
    nextRegions: Record<string, string>,
): boolean {
    const prevRegionsIds = Object.keys(prevRegions);
    const nextRegionsIds = Object.keys(nextRegions);

    if (prevRegionsIds.length !== nextRegionsIds.length) {
        return false;
    }

    return prevRegionsIds.every((key) => prevRegions[key] === nextRegions[key]);
}

export type UseBusinessGeoOptions = {
    regionsLimit?: number;
};

export const useBusinessGeo = (
    regions?: BusinessGeoDto[],
    { regionsLimit }: UseBusinessGeoOptions = {},
) => {
    const t = useTranslation();
    const dispatch = useDispatch();
    const [geoChanged, setGeoChanged] = useState(false);
    const geo = useSelector((state) => selectGeo(state));
    const [regionQuery, setRegionQuery] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const onLoadGeoSuggests = useMemo(
        () =>
            debounce((query?: string) => {
                dispatch(loadGeoSuggestsAsync(query));
            }, TIMEOUT),
        [dispatch],
    );

    const initialSelectedRegions = getRegionsSettings(regions);
    const [selectedRegions, setSelectedRegions] =
        useState<Record<string, string>>(initialSelectedRegions);

    const onSelectRegion = useCallback(
        (value: Record<string, string>) => {
            const regionKey = Object.keys(value)[0];
            const newSelectedRegions = { ...selectedRegions };

            if (regionKey in selectedRegions) {
                delete newSelectedRegions[regionKey];
            } else {
                newSelectedRegions[regionKey] = value[regionKey];
            }

            if (
                regionsLimit !== undefined &&
                Object.keys(newSelectedRegions).length > regionsLimit
            ) {
                setErrorMessage(t(`Можно выбрать не более ${regionsLimit} регионов`));

                return;
            }

            setErrorMessage(undefined);
            setSelectedRegions(newSelectedRegions);
            setRegionQuery('');
            onLoadGeoSuggests(DEFAULT_GEO_NAME);

            const isChanged = !areRegionsEqual(initialSelectedRegions, newSelectedRegions);

            setGeoChanged(isChanged);
        },
        [selectedRegions, initialSelectedRegions, regionsLimit, t, onLoadGeoSuggests],
    );

    const onRemoveRegion = useCallback(
        (key: string) => {
            const newSelectedRegions = { ...selectedRegions };

            delete newSelectedRegions[key];

            setSelectedRegions(newSelectedRegions);

            setErrorMessage(undefined);

            const isChanged = !areRegionsEqual(initialSelectedRegions, newSelectedRegions);

            setGeoChanged(isChanged);
        },
        [selectedRegions, initialSelectedRegions],
    );

    const onInputRegion = useCallback(
        (event: FormEvent<HTMLInputElement>) => {
            const value = event.currentTarget.value;

            setRegionQuery(value);
            onLoadGeoSuggests(value);
            setErrorMessage(undefined);

            if (!value) {
                onLoadGeoSuggests(DEFAULT_GEO_NAME);
            }
        },
        [onLoadGeoSuggests],
    );

    const updateSelectedRegions = useCallback((newRegions?: BusinessGeoDto[]) => {
        setSelectedRegions(getRegionsSettings(newRegions));
    }, []);

    useEffectOnce(() => {
        onLoadGeoSuggests(DEFAULT_GEO_NAME);
    });

    return {
        selectedRegions,
        geoSuggests: geo.suggests,
        regionQuery,
        geoChanged,
        errorMessage,
        onSelectRegion,
        onRemoveRegion,
        onInputRegion,
        updateSelectedRegions,
    };
};
